<template>
  <v-main>
    <v-container fluid class="pa-6 pl-page">
      <!-- Header -->
      <div class="pl-head">
        <div>
          <h1 class="pl-title">{{ isDeletePage() ? "Deleted Job Positions" : "Job Positions" }}</h1>
          <p class="pl-sub mb-0" v-if="!isDeletePage()">
            A position decides what a person can do. Franchise partners work in the admin panel, with the
            screens and reports you give them; customers and walkers use the mobile app.
          </p>
        </div>
        <v-btn v-if="!isDeletePage()" color="primary" depressed to="/positions/create">
          <v-icon left>mdi-plus</v-icon>Add Position
        </v-btn>
      </div>

      <!-- Filters -->
      <div class="pl-filters">
        <v-text-field
          v-model="filter.search_keyword"
          placeholder="Search positions"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
          class="pl-search"
          @click:clear="(filter.search_keyword = null), (page = 1), getData()"
          @keydown.enter="(page = 1), getData()"
        />
        <v-select
          v-model="filter.is_active"
          :items="statuses"
          placeholder="All statuses"
          prepend-inner-icon="mdi-toggle-switch-outline"
          outlined
          dense
          hide-details
          clearable
          class="pl-status"
          @click:clear="(filter.is_active = null), (page = 1)"
          @input="(page = 1), getData()"
        />
      </div>

      <!-- Admins always have everything -->
      <div class="pl-admins" v-if="!isDeletePage()">
        <v-icon color="primary">mdi-shield-crown-outline</v-icon>
        <div>
          <strong>Admin</strong> and <strong>Super Admin</strong> are roles, not positions: they always have full
          access to every screen and report.
        </div>
      </div>

      <v-progress-linear v-if="isLoading" indeterminate color="primary" class="mb-4" />

      <!-- Position cards -->
      <v-row v-if="positions.length" dense>
        <v-col v-for="position in positions" :key="position.id" cols="12" md="6" xl="4">
          <div class="pl-card" :class="{ 'is-inactive': position.is_active == 0 }">
            <div class="pl-card__top">
              <div class="pl-card__icon" :class="isPanel(position) ? 'is-panel' : 'is-app'">
                <v-icon>{{ isPanel(position) ? "mdi-monitor-dashboard" : "mdi-cellphone" }}</v-icon>
              </div>
              <div class="pl-card__heading">
                <div class="pl-card__name">{{ titleCase(position.name) }}</div>
                <div class="pl-card__type">
                  {{ isPanel(position) ? "Admin panel" : "Mobile app" }}
                  <span class="pl-dot">&middot;</span>
                  Added {{ formatDate(position.created_at) }}
                </div>
              </div>
              <span class="pl-status-chip" :class="position.is_active == 1 ? 'is-on' : 'is-off'">
                {{ position.is_active == 1 ? "Active" : "Inactive" }}
              </span>
            </div>

            <!-- What the position can reach -->
            <div class="pl-card__body">
              <template v-if="isPanel(position)">
                <div class="pl-stats">
                  <div class="pl-stat">
                    <div class="pl-stat__value">
                      {{ position.access_summary.screens }}<span>/{{ position.access_summary.total_screens }}</span>
                    </div>
                    <div class="pl-stat__label">Screens</div>
                  </div>
                  <div class="pl-stat">
                    <div class="pl-stat__value">
                      {{ position.access_summary.reports }}<span>/{{ position.access_summary.total_reports }}</span>
                    </div>
                    <div class="pl-stat__label">Reports</div>
                  </div>
                </div>
                <div class="pl-levels">
                  <span v-for="lvl in levelOrder" :key="lvl.value" class="pl-level" v-show="levelCount(position, lvl.value)">
                    <span class="pl-level__dot" :class="`is-${lvl.value}`"></span>
                    {{ levelCount(position, lvl.value) }} {{ lvl.text }}
                  </span>
                  <span v-if="!position.access_summary.screens" class="pl-muted">No screens given yet</span>
                </div>
              </template>
              <p v-else class="pl-muted mb-0">
                Signs in to the Walkies mobile app; has no admin panel access to set.
              </p>
            </div>

            <div class="pl-card__actions">
              <template v-if="!isDeletePage()">
                <v-btn
                  v-if="!isSystem(position)"
                  text
                  small
                  color="error"
                  @click="OpenWarning(position.id)"
                >
                  <v-icon left small>mdi-delete-outline</v-icon>Delete
                </v-btn>
                <span v-else class="pl-muted pl-system">
                  <v-icon x-small>mdi-lock-outline</v-icon> System position
                </span>
                <v-spacer />
                <v-btn depressed small color="primary" :to="`/positions/${encryptIt(position.id)}`">
                  <v-icon left small>{{ isPanel(position) ? "mdi-shield-edit-outline" : "mdi-pencil-outline" }}</v-icon>
                  {{ isPanel(position) ? "Manage access" : "Edit" }}
                </v-btn>
              </template>
              <template v-else>
                <v-spacer />
                <v-btn depressed small color="primary" @click="Restore_this(position.id)">
                  <v-icon left small>mdi-delete-restore</v-icon>Restore
                </v-btn>
              </template>
            </div>
          </div>
        </v-col>
      </v-row>

      <div v-else-if="!isLoading" class="pl-empty">
        <v-icon size="40" color="grey lighten-1">mdi-badge-account-outline</v-icon>
        <p class="pl-muted mb-0">No positions found</p>
      </div>

      <v-pagination
        v-if="paginationLength > 1"
        v-model="page"
        :length="paginationLength"
        class="mt-4"
      />

      <!-- Delete confirmation -->
      <v-dialog v-model="deleteDialog" max-width="420">
        <v-card>
          <v-card-title class="text-h6">Delete this position?</v-card-title>
          <v-card-text>
            People with this position will lose what it gives them. You can restore it later from the
            deleted positions.
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text @click="deleteDialog = false">Cancel</v-btn>
            <v-btn
              color="error"
              depressed
              :loading="delete_position"
              @click="Delete_this(to_be_deleted_id), (deleteDialog = false)"
            >
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000">
        {{ snackbar.text }}
      </v-snackbar>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

// The app looks these up by name, so they cannot be deleted from here
const SYSTEM_POSITIONS = ["MASTER FRANCHISEE", "FRANCHISEE ADMIN", "CUSTOMER", "FRANCHISEE STAFF"];

export default {
  name: "positionsPage",
  data() {
    return {
      filter: {
        search_keyword: "",
        is_active: "",
        show_deleted: false,
      },
      positions: [],
      statuses: [
        { text: "Active", value: "1" },
        { text: "Inactive", value: "0" },
      ],
      levelOrder: [
        { value: "full", text: "full access" },
        { value: "edit", text: "add & edit" },
        { value: "view", text: "view only" },
      ],
      page: 1,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      isLoading: false,
      delete_position: false,
      restore_position: false,
      deleteDialog: false,
      to_be_deleted_id: "",
      snackbar: { show: false, text: "", color: "success" },
    };
  },
  watch: {
    page: "getData",
    $route() {
      this.getData();
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    titleCase(text) {
      return String(text || "")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatDate(value) {
      if (!value) return "";
      const d = new Date(value);
      if (isNaN(d)) return value;
      return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    isPanel(position) {
      return !!(position.access_summary && position.access_summary.is_panel_position);
    },
    isSystem(position) {
      return SYSTEM_POSITIONS.includes(position.name);
    },
    levelCount(position, level) {
      const levels = (position.access_summary && position.access_summary.levels) || {};
      return levels[level] || 0;
    },
    notify(text, color = "success") {
      this.snackbar = { show: true, text, color };
    },
    OpenWarning(request_id) {
      this.to_be_deleted_id = request_id;
      this.deleteDialog = true;
    },
    async getData() {
      this.isLoading = true;
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.filter.search_keyword = this.filter.search_keyword ? this.filter.search_keyword : "";
      this.filter.is_active = this.filter.is_active ? this.filter.is_active : "";
      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.rowsPerPage +
        "&show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&search_keyword=" +
        this.filter.search_keyword;
      await axios
        .get(`positions?${query}`)
        .then((response) => {
          this.positions = response.data.data;
          this.count = response.data.count;
          this.serialNoStarting = (this.page - 1) * this.rowsPerPage;
          this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
        })
        .catch(function (error) {
          console.log(error);
          window.location.href = "/auth/login";
        });
      this.isLoading = false;
    },
    async Delete_this(id) {
      this.delete_position = true;
      await axios
        .post(`/positions/delete/${this.encryptIt(id)}`)
        .then(() => {
          this.delete_position = false;
          this.notify("Position deleted");
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          window.location.href = "/auth/login";
        });
    },
    async Restore_this(id) {
      this.restore_position = true;
      await axios
        .post(`/positions/restore/${this.encryptIt(id)}`)
        .then(() => {
          this.restore_position = false;
          this.notify("Position restored");
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          window.location.href = "/auth/login";
        });
    },
  },
};
</script>

<style scoped>
.pl-page {
  max-width: 1400px;
}
.pl-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}
.pl-title {
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
}
.pl-sub {
  color: #66738a;
  font-size: 13px;
  margin-top: 4px;
  max-width: 720px;
}
.pl-filters {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.pl-search {
  flex: 0 1 360px;
  background: #fff;
}
.pl-status {
  flex: 0 0 200px;
  background: #fff;
}
.pl-admins {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #eef3fb;
  border-radius: 12px;
  padding: 10px 16px;
  font-size: 13px;
  color: #33405a;
  margin-bottom: 16px;
}
.pl-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
  border: 1px solid #e4e8ee;
  border-radius: 14px;
  transition: box-shadow 0.15s, border-color 0.15s;
}
.pl-card:hover {
  border-color: #c3d3ec;
  box-shadow: 0 4px 14px rgba(16, 24, 40, 0.07);
}
.pl-card.is-inactive {
  opacity: 0.7;
}
.pl-card__top {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px 12px;
}
.pl-card__icon {
  flex: 0 0 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pl-card__icon.is-panel {
  background: #eef3fb;
}
.pl-card__icon.is-panel .v-icon {
  color: #153a6e;
}
.pl-card__icon.is-app {
  background: #f1f3f6;
}
.pl-card__icon.is-app .v-icon {
  color: #66738a;
}
.pl-card__heading {
  flex: 1 1 auto;
  min-width: 0;
}
.pl-card__name {
  font-size: 16px;
  font-weight: 700;
  color: #1a2436;
}
.pl-card__type {
  font-size: 12px;
  color: #8d99ad;
}
.pl-dot {
  margin: 0 4px;
}
.pl-status-chip {
  font-size: 11px;
  font-weight: 600;
  border-radius: 999px;
  padding: 3px 10px;
  white-space: nowrap;
}
.pl-status-chip.is-on {
  background: #e6f6ec;
  color: #0a7a33;
}
.pl-status-chip.is-off {
  background: #fdecec;
  color: #b42318;
}
.pl-card__body {
  flex: 1 1 auto;
  padding: 0 18px 14px;
}
.pl-stats {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}
.pl-stat {
  flex: 1 1 0;
  background: #f6f8fb;
  border-radius: 10px;
  padding: 8px 12px;
}
.pl-stat__value {
  font-size: 20px;
  font-weight: 700;
  color: #153a6e;
}
.pl-stat__value span {
  font-size: 13px;
  font-weight: 500;
  color: #8d99ad;
}
.pl-stat__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #8d99ad;
}
.pl-levels {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  font-size: 12px;
  color: #33405a;
}
.pl-level {
  display: inline-flex;
  align-items: center;
}
.pl-level__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}
.pl-level__dot.is-view {
  background: #2a78d6;
}
.pl-level__dot.is-edit {
  background: #eda100;
}
.pl-level__dot.is-full {
  background: #0ca30c;
}
.pl-muted {
  color: #8d99ad;
  font-size: 13px;
}
.pl-card__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid #f0f2f6;
}
.pl-system {
  font-size: 12px;
  padding-left: 4px;
}
.pl-empty {
  text-align: center;
  padding: 48px 16px;
}
</style>
